<template>
  <vx-card
    class="user-account-company-policies"
    no-shadow
  >
    <div class="flex justify-center">
      <form
        class="w-full lg:w-1/2 xl:w-1/2"
        @submit.prevent>

        <h3 >
          {{ $t('$UserSettings.$Account.$CompanyPolicies.TabTitle') }}
        </h3>

        <card-radio-toggle
          ref="messageTypeRadioToggle"
          v-model="model.policyType"
          :radio-name="$tc('$General.Type')"
          :radio-options="policyTypeOptions"
          :show-label="true"
          icon="InfoIcon"
          class="mt-5"
          :icon-msg="$t('InlinePolicyInputRadioTypeText')"
          required/>

        <app-alert
          v-if="model.policyType === $enums.Tenants.POLICY_TYPE.GENERATED && !isAccountDetailsComplete"
          icon-pack="feather"
          icon="icon-alert-triangle"
          color="warning"
          class="mt-base"
          type="default">
          <div class="pt-2">
            <span>{{ $t('AccountDetailsIncompleteAlertMsg') }}</span>
            <a
              href="#"
              class="link-cta ml-1"
              @click.prevent="goToAccountDetails"
            >
              {{ $t('AccountDetailsIncompleteAlertLink') }}
            </a>.
          </div>
        </app-alert>

        <app-alert
          v-if="model.policyType === $enums.Tenants.POLICY_TYPE.GENERATED && isAccountDetailsComplete"
          icon-pack="feather"
          icon="icon-alert-triangle"
          color="warning"
          class="mt-base"
          type="default">
          <div class="pt-2">
            <span>{{ $t('GeneratedPrivacyPolicyDisclaimerMsg') }}</span>
            <a
              href="#"
              class="link-popup ml-0"
              @click.prevent="activeGeneratePrivacyPolicyDisclaimerModal = true"
            >
              {{ $t('GeneratedPrivacyPolicyDisclaimerLink') }}
            </a>.
          </div>
        </app-alert>

        <div v-show="model.policyType === $enums.Tenants.POLICY_TYPE.INLINE">
          <quill-editor
            v-model="model.inlinePolicy"
            :options="editorOption"
            class="mt-base"
          ></quill-editor>

          <vs-input
            v-model="model.inlinePolicy"
            type="hidden"
            class="w-full"
            :name="$t('InlinePolicyInputName')"
            v-validate="model.policyType === $enums.Tenants.POLICY_TYPE.INLINE
            ? 'required' : ''"
            data-vv-validate-on="blur|input"
            :danger="errors.has($t('InlinePolicyInputName'))"
            :danger-text="errors.first($t('InlinePolicyInputName'))"/>
        </div>

        <div v-show="model.policyType === $enums.Tenants.POLICY_TYPE.GENERATED">
          <quill-editor
            v-model="model.generatedPolicy"
            :options="generatedPolicyEditorOption"
            :class="['mt-base', { 'editor-disabled': !isAccountDetailsComplete }]"
          ></quill-editor>

          <vs-input
            v-model="model.generatedPolicy"
            type="hidden"
            class="w-full"
            :name="$t('GeneratedPolicyInputName')"
            v-validate="model.policyType === $enums.Tenants.POLICY_TYPE.GENERATED
            ? 'required' : ''"
            data-vv-validate-on="blur|input"
            :danger="errors.has($t('GeneratedPolicyInputName'))"
            :danger-text="errors.first($t('GeneratedPolicyInputName'))"/>
        </div>

        <vs-input
          v-show="model.policyType === $enums.Tenants.POLICY_TYPE.URL"
          v-model="model.linkPolicy"
          class="w-full required mt-base"
          :name="$t('LinkPolicyInputName')"
          :label="$t('LinkPolicyInputName')"
          :placeholder="$t('LinkPolicyInputPlaceholder')"
          v-validate="model.policyType === $enums.Tenants.POLICY_TYPE.URL
          ? 'required|url' : 'url'"
          data-vv-validate-on="blur|input"
          icon="icon icon-link"
          icon-pack="feather"
          :danger="errors.has($t('LinkPolicyInputName'))"
          :danger-text="errors.first($t('LinkPolicyInputName'))"
          val-icon-danger="clear"/>
        <vs-divider/>

        <div v-if="!asWizardStep" class="flex flex-wrap gap-3">
          <vs-button
            class="w-full md:w-auto"
            @click="savePolicy">
            {{ $t('$General.Save') }}
          </vs-button>
          
          <vs-button
            v-if="model.policyType === $enums.Tenants.POLICY_TYPE.GENERATED"
            color="primary"
            type="border"
            class="w-full md:w-auto"
            @click="regenerateTemplate">
            Reset to default template
          </vs-button>
        </div>
      </form>

      <vs-popup
        :title="$t('GeneratedPrivacyPolicyDisclaimerModalTitle')"
        :active="activeGeneratePrivacyPolicyDisclaimerModal"
        @close="activeGeneratePrivacyPolicyDisclaimerModal = false"
      >
        <div v-html="$t('GeneratedPrivacyPolicyDisclaimerModalMsg')"></div>
      </vs-popup>
    </div>
  </vx-card>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { quillEditor } from 'vue-quill-editor';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.core.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.snow.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'quill/dist/quill.bubble.css';
import CardRadioToggle from '@/views/modules/_components/CardRadioToggle.vue';
import tenantService from '@/api/modules/tenant.service';
import defaultGeneratedPolicy from './default-generated-policy';
import allCountries from '@/assets/utils/all-countries';

// Components
import AppAlert from '@/views/_components/AppAlert.vue';

export default {
  name: 'UserAccountCompanyPolicies',
  i18n: {
    messages: {
      en: {
        LinkPolicyInputName: 'Policy URL',
        LinkPolicyInputPlaceholder: 'Insert your Privacy Policy URL',

        InlinePolicyInputName: 'Written policy',
        GeneratedPolicyInputName: 'Generated policy',
        InlinePolicyInputPlaceholder: 'If you don\'t have a Privacy Policy URL for your website, you can write your policy here, and GrapePerks will host it for you at a unique URL.',

        InlinePolicyInputRadioTypeText: 'Link to your Privacy Policy, write your own, or use a basic template (not legal advice). GrapePerks will host written policies at a unique URL.',

        InlinePolicyInputRadioText: 'Write policy',
        URLPolicyInputRadioText: 'Use URL',
        GeneratedPolicyInputRadioText: 'Generate',

        AccountDetailsIncompleteTitle: 'Complete account details required',
        AccountDetailsIncompleteMsg: 'You need to complete your account details before generating a privacy policy. This ensures all your business information is included in the template.',

        AccountDetailsIncompleteAlertMsg: 'You need to complete your account details before generating a privacy policy.',
        AccountDetailsIncompleteAlertLink: 'Complete account details',

        AccountDetailsRequiredPlaceholder: 'Complete your account details to generate a privacy policy template.',

        UpdatedPrivacyPolicyTitle: 'Updated privacy policy',
        UpdatedPrivacyPolicyMsg: 'Privacy policy updated successfully.',

        GeneratedPrivacyPolicyDisclaimerMsg: 'Our privacy policy template is provided as a helpful tool and does not constitute legal advice. By using it, you acknowledge and agree to ',
        GeneratedPrivacyPolicyDisclaimerLink: 'this disclaimer',
        GeneratedPrivacyPolicyDisclaimerModalTitle: 'Legal disclaimer',
        GeneratedPrivacyPolicyDisclaimerModalMsg: "<p>This template is for informational purposes only and does not constitute legal advice. By using this template, you agree to this disclaimer. You should seek independent legal advice before publishing any policies or agreements based on this template. It is your responsibility to review, modify, or remove any sections as needed to ensure compliance with applicable laws.</p><p class=\"mt-5\">Company details (such as business name, email, and address) are automatically populated only when the template is first generated. Any subsequent updates to your company information will need to be manually updated in your privacy policy. We recommend reviewing and updating your policy whenever your company details change.</p><p class=\"mt-5\">Use of this template does not create an attorney-client relationship between GrapePerks and any user. You should not rely on this information as a substitute for legal advice from a qualified professional in your jurisdiction. GrapePerks makes no guarantees regarding the accuracy, completeness, or legal compliance of this template and disclaims any liability for actions taken or not taken based on its contents. Additionally, GrapePerks is not responsible for any third-party content linked or referenced in relation to this template.</p>",
      },
    },
  },
  components: {
    CardRadioToggle,
    quillEditor,
    AppAlert,
  },
  props: {
    asWizardStep: {
      type: Boolean,
      require: false,
      default: false,
    },
  },
  data() {
    return {
      payload: null,
      model: {
        policyType: this.$enums.Tenants.POLICY_TYPE.URL,
        inlinePolicy: '',
        linkPolicy: '',
        generatedPolicy: '',
      },

      activeGeneratePrivacyPolicyDisclaimerModal: false,

      editorOption: {
        placeholder: this.$t('InlinePolicyInputPlaceholder'),
      },
      
      validationFields: {},
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountBusinessName: 'auth/tenantAccountBusinessName',
      tenantAccountBusinessEmail: 'auth/tenantAccountBusinessEmail',
      tenantAccountBusinessFullAddress: 'auth/tenantAccountBusinessFullAddress',
      tenantAccountBusinessWebsite: 'auth/tenantAccountBusinessWebsite',
      tenantAccountDetailsCountry: 'auth/tenantAccountDetailsCountry',
      getTenantFlag: 'auth/getTenantFlag',
    }),
    isAccountDetailsComplete() {
      return this.getTenantFlag('completedAccountDetails');
    },
    policyTypeOptions() {
      return [
        {
          value: this.$enums.Tenants.POLICY_TYPE.URL,
          label: this.$t('URLPolicyInputRadioText'),
        },
        {
          value: this.$enums.Tenants.POLICY_TYPE.INLINE,
          label: this.$t('InlinePolicyInputRadioText'),
        },
        {
          value: this.$enums.Tenants.POLICY_TYPE.GENERATED,
          label: this.$t('GeneratedPolicyInputRadioText'),
        },
      ];
    },
    getCountryFullName() {
      if (!this.tenantAccountDetailsCountry) return '{{COUNTRY}}';
      
      const country = allCountries.find(
        (c) => c.iso2 === this.tenantAccountDetailsCountry.toUpperCase()
      );
      return country ? country.name : this.tenantAccountDetailsCountry;
    },
    defaultGeneratedPolicyMapped() {
      let template = defaultGeneratedPolicy;
      
      // Handle handlebars conditionals for WEBSITE
      const hasWebsite = !!(this.tenantAccountBusinessWebsite && this.tenantAccountBusinessWebsite.trim());
      
      // Process {{#if WEBSITE}} conditionals (using [\s\S] instead of dotAll flag)
      template = template.replace(/\{\{#if WEBSITE\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, content) => {
        return hasWebsite ? content : '';
      });
      
      // Process {{#if WEBSITE}}...{{else}}...{{/if}} conditionals
      template = template.replace(/\{\{#if WEBSITE\}\}([\s\S]*?)\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, ifContent, elseContent) => {
        return hasWebsite ? ifContent : elseContent;
      });
      
      // Replace variables
      return template
        .replace(/\{\{BUSINESS\}\}/g, this.tenantAccountBusinessName || '{{BUSINESS}}')
        .replace(/\{\{DATE\}\}/g, moment().format('DD/MM/YYYY'))
        .replace(/\{\{EMAIL\}\}/g, this.tenantAccountBusinessEmail || '{{EMAIL}}')
        .replace(/\{\{ADDRESS\}\}/g, this.tenantAccountBusinessFullAddress || '{{ADDRESS}}')
        .replace(/\{\{WEBSITE\}\}/g, this.tenantAccountBusinessWebsite || '{{WEBSITE}}')
        .replace(/\{\{COUNTRY\}\}/g, this.getCountryFullName);
    },
    formWasChanged() {
      return Object.keys(this.validationFields).some(
        (key) => this.validationFields[key].changed,
      ) || (!!this.payload && (
        this.payload.policyType !== this.model.policyType
        || this.payload.inlinePolicy !== this.model.inlinePolicy
        || this.payload.linkPolicy !== this.model.linkPolicy
      ));
    },
    generatedPolicyEditorOption() {
      return {
        readOnly: !this.isAccountDetailsComplete,
        placeholder: this.isAccountDetailsComplete 
          ? this.$t('InlinePolicyInputPlaceholder')
          : this.$t('AccountDetailsRequiredPlaceholder'),
      };
    },
  },
  async created() {
    if (!this.asWizardStep) {
      window.history.replaceState({}, null, '/account/policies');
    }

    await this.fetchPrivacyPolicySettings();
    await this.$validator.reset();
  },
  methods: {
    ...mapActions({
      updateTenantAccountPrivacyPolicySettings: 'auth/updateTenantAccountPrivacyPolicySettings',
    }),
    async fetchPrivacyPolicySettings() {
      this.$vs.loading({ type: 'radius' });
      const resp = await tenantService.getAccountSettingsPrivacyPolicy();
      this.$vs.loading.close();
      this.payload = resp.data;

      this.setModel();
    },
    setModel() {
      this.model.policyType = this.payload
        ? this.payload.policyType || this.model.policyType
        : this.model.policyType;

      this.model.inlinePolicy = this.payload
        ? this.payload.inlinePolicy || this.model.inlinePolicy
        : this.model.inlinePolicy;

      // Only use defaultGeneratedPolicyMapped if there's no saved generatedPolicy
      // This allows users to edit the template after first generation
      if (this.payload && this.payload.generatedPolicy) {
        // User has previously saved a generated policy, use their edited version
        this.model.generatedPolicy = this.payload.generatedPolicy;
      } else {
        // First time or no saved policy, use the processed template
        this.model.generatedPolicy = this.defaultGeneratedPolicyMapped;
      }

      this.model.linkPolicy = this.payload
        ? this.payload.linkPolicy || this.model.linkPolicy
        : this.model.linkPolicy;
    },
    regenerateTemplate() {
      // Force regeneration of the template with current company details
      this.model.generatedPolicy = this.defaultGeneratedPolicyMapped;
    },
    async savePolicy() {
      const formIsValid = await this.$validator.validateAll();

      if (this.model.policyType === this.$enums.Tenants.POLICY_TYPE.URL) {
        this.model.inlinePolicy = '';
      } else {
        this.model.linkPolicy = '';
      }

      if (formIsValid) {
        this.$vs.loading({ type: 'radius' });
        const resp = await this.updateTenantAccountPrivacyPolicySettings(this.model);
        this.$vs.loading.close();
        
        // Update payload but DON'T call setModel() to avoid overriding user's changes
        this.payload = resp.data;

        this.$showSuccessActionNotification({
          title: this.$t('UpdatedPrivacyPolicyTitle'),
          text: this.$t('UpdatedPrivacyPolicyMsg'),
        });
      }

      return formIsValid;
    },
    goToAccountDetails() {
      this.$root.$emit('switch-to-account-tab', 'details');
    },
  },
  watch: {
    'model.policyType'(newValue, oldValue) {
      // If user tries to select "Generate" when account details are incomplete
      if (newValue === this.$enums.Tenants.POLICY_TYPE.GENERATED && !this.isAccountDetailsComplete) {
        // Don't prevent the selection anymore since we show the banner
        // The banner will guide them to complete account details
      }
    },
  },
};
</script>

<style lang="scss">
.user-account-company-policies {
  .quill-editor {
    min-height: 250px;
  }
  .ql-container {
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
  }
  
  // Disabled editor styling
  .editor-disabled {
    opacity: 0.6;
    pointer-events: none;
    
    .ql-toolbar {
      opacity: 0.5;
      pointer-events: none;
    }
    
    .ql-container {
      background-color: #f8f9fa;
      color: #6c757d;
    }
    
    .ql-editor {
      cursor: not-allowed;
      background-color: #f8f9fa !important;
    }
  }
}
</style>
